<script setup lang="ts">
import StepDomainInput from './components/StepDomainInput.vue'
import StepDnsProvider from './components/StepDnsProvider.vue'
import StepDnsConfig from './components/StepDnsConfig.vue'
import DnsConnectStatus from './components/DnsConnectStatus.vue'
import Forbidden from '@/components/errors/403.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { createDomain, getDnsRecords, currentDomain, dnsRecords, isLoadingDomain, listDomains, verifyDomain } =
  useDomainConfig()
const { isDirection } = useAccess()

const domain = ref('')
const currentStep = ref(1)
const isExistingDomain = ref(false)
const isReady = ref(false)
const showDnsConnect = computed(() => !!route.query.dnsConnect && !!currentDomain.value?.id)

const handleDnsConnectApplied = async () => {
  if (!currentDomain.value?.id) return

  await getDnsRecords(currentDomain.value.id)
  await verifyDomain(currentDomain.value.id)
  startPolling()
}

const pageTitle = computed(() => ({
  title: t('pages.domain.config.title')
}))

const steps = computed(() => [
  { step: 1, title: t('pages.domain.config.steps.domain.stepTitle') },
  { step: 2, title: t('pages.domain.config.steps.provider.stepTitle') },
  { step: 3, title: t('pages.domain.config.steps.dns.stepTitle') }
])

const crumbs = computed(() => [
  {
    name: t('pages.domain.title'),
    route: 'DomainSettings'
  },
  {
    name: t('pages.domain.config.title')
  }
])

const handleCancel = () => {
  router.push({ name: 'DomainSettings' })
}

const handleStep1Next = async () => {
  if (isExistingDomain.value) {
    currentStep.value = 2
    return
  }

  const result = await createDomain(domain.value)

  if (result) {
    currentStep.value = 2
  }
}

const handleStep2Next = async () => {
  if (currentDomain.value?.id && !dnsRecords.value) {
    await getDnsRecords(currentDomain.value.id)
  }
  currentStep.value = 3
}

const handleFinish = () => {
  stopPolling()
  router.push({ name: 'DomainSettings' })
}

// Polling pour vérifier le statut du domaine
const POLLING_INTERVAL = 15000 // 15 secondes
const MAX_POLLING_DURATION = 5 * 60 * 1000 // 5 minutes
let pollingInterval: ReturnType<typeof setInterval> | null = null
let pollingStartTime: number | null = null

const startPolling = () => {
  if (pollingInterval) return

  pollingStartTime = Date.now()
  pollingInterval = setInterval(async () => {
    const elapsedTime = Date.now() - (pollingStartTime || 0)

    // Arrête le polling si le temps max est dépassé
    if (elapsedTime >= MAX_POLLING_DURATION) {
      stopPolling()
      return
    }

    // Ne fait l'appel que si le domaine n'est pas encore prêt
    if (currentDomain.value?.id && !currentDomain.value.is_ready_for_sending) {
      await verifyDomain(currentDomain.value.id)
      // Met à jour currentDomain avec les nouvelles données
      const domains = await listDomains()
      const updatedDomain = domains.find((d) => d.id === currentDomain.value?.id)
      if (updatedDomain) {
        currentDomain.value = updatedDomain
      }
    } else {
      stopPolling()
    }
  }, POLLING_INTERVAL)
}

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval)
    pollingInterval = null
    pollingStartTime = null
  }
}

watch(
  () => currentDomain.value?.is_ready_for_sending,
  (isReady) => {
    if (isReady) {
      stopPolling()
    }
  }
)

onUnmounted(() => {
  stopPolling()
})

onMounted(async () => {
  const domainId = route.query.domainId as string
  if (domainId) {
    const domains = await listDomains()
    const existingDomain = domains.find((d) => d.id === Number(domainId))
    if (existingDomain) {
      currentDomain.value = existingDomain
      domain.value = existingDomain.domain
      await getDnsRecords(existingDomain.id)
      currentStep.value = 3
      isExistingDomain.value = true

      // Démarre le polling si le domaine n'est pas encore prêt
      if (!existingDomain.is_ready_for_sending) {
        startPolling()
      }
    }
  }
  isReady.value = true
})
</script>

<template>
  <Forbidden v-if="!isDirection" :title="pageTitle.title" :crumbs="crumbs" />

  <div v-else>
    <TheDetailHeader
      :title="pageTitle.title"
      :parent="{ label: t('pages.domain.title'), to: { name: 'DomainSettings' } }"
    />

    <div class="px-4 sm:px-12 pb-6 sm:pb-12">
      <template v-if="isReady">
        <DnsConnectStatus
          v-if="showDnsConnect && currentDomain"
          :email-domain-id="currentDomain.id"
          class="mb-6"
          @applied="handleDnsConnectApplied"
        />
        <Stepper v-model="currentStep" orientation="vertical" class="flex w-full flex-col gap-4">
          <StepperItem
            v-for="step in steps"
            :key="step.step"
            v-slot="{ state }"
            :step="step.step"
            class="relative flex w-full flex-col !items-stretch gap-3"
          >
            <StepperSeparator
              v-if="step.step !== steps[steps.length - 1]?.step"
              class="absolute left-[15px] top-8 -bottom-4 h-auto w-0.5 flex-none rounded-full bg-stone-200 group-data-[state=completed]:bg-stone-200"
            />
            <StepperTrigger class="flex w-fit cursor-pointer flex-row items-center gap-3 p-0">
              <StepperIndicator
                class="size-8 rounded-full border bg-transparent text-sm font-medium"
                :class="[
                  state === 'active' && 'border-primary text-primary',
                  state !== 'active' && 'border-border text-muted-foreground'
                ]"
              >
                {{ step.step }}
              </StepperIndicator>
              <StepperTitle
                class="text-sm font-semibold transition"
                :class="[state === 'active' ? 'text-primary' : 'text-foreground']"
              >
                {{ step.title }}
              </StepperTitle>
            </StepperTrigger>

            <!-- Active step panel -->
            <Card v-if="state === 'active'" class="ml-10">
              <CardContent>
                <StepDomainInput
                  v-if="step.step === 1"
                  v-model="domain"
                  :is-loading="isLoadingDomain"
                  :disabled="isExistingDomain"
                  @next="handleStep1Next"
                  @cancel="handleCancel"
                />
                <StepDnsProvider
                  v-else-if="step.step === 2"
                  :domain="domain"
                  :email-domain-id="currentDomain?.id"
                  @next="handleStep2Next"
                  @back="currentStep = 1"
                />
                <StepDnsConfig
                  v-else-if="step.step === 3"
                  :domain="domain"
                  :dns-records="dnsRecords"
                  :domain-status="currentDomain"
                  :show-status="isExistingDomain"
                  @back="currentStep = 2"
                  @finish="handleFinish"
                />
              </CardContent>
            </Card>
          </StepperItem>
        </Stepper>
      </template>
    </div>
  </div>
</template>
